import { boundedText } from "./bounded-text.js";
import { catalogFulfilmentModes, catalogItemTypes, catalogMediaKinds, catalogPersonRoles, catalogSlug, catalogSourceKey, catalogSourceKinds, catalogSourceSystem, catalogTagNamespaces, diningDressCodes, diningReservationPolicies, eventSchedulePrecisions, eventStatuses, experiencePrivacies, isFulfilmentModeEnabled, } from "./catalog.js";
import { validateCatalogPricing, } from "./catalog-pricing.js";
import { DomainValidationError } from "./identifiers.js";
import { minorUnits, supportedCurrency } from "./money.js";
import { ianaTimeZone, isLocalMidnight, localTimeOfDay } from "./time-zone.js";
import { isMember, violation } from "./transition-support.js";
// The shape of a new catalog item as a catalog editor submits it, and the validated draft the
// repository writes. It mirrors the database CHECKs column by column, so the database is the
// backstop and not the first line of defence. A draft never carries payment authority: the
// authority columns are not part of it, and `price_authoritative` starts false.
const MAX_CATEGORIES = 20;
const MAX_TAGS = 60;
const MAX_MEDIA = 100;
const MAX_PEOPLE = 100;
const MAX_EXTERNAL_REFS = 10;
const MAX_SOURCES = 10;
/** The bounds of the link lists, shared with the editing commands and the staff API schemas (parity-tested). */
export const CATALOG_LIST_LIMITS = Object.freeze({
    categories: MAX_CATEGORIES,
    tags: MAX_TAGS,
    media: MAX_MEDIA,
    people: MAX_PEOPLE,
    externalRefs: MAX_EXTERNAL_REFS,
    sources: MAX_SOURCES,
});
const COUNTRY = /^[A-Z]{2}$/;
const CODE = /^[a-z0-9_]{1,64}$/;
const PROVIDER = /^[a-z][a-z0-9_]*$/;
const MEDIA_PATH = /^[A-Za-z0-9/_.-]+$/;
const EXTERNAL_URL = /^https:\/\/[^\s]+$/;
const fail = (field, rule) => new DomainValidationError(field, rule);
const present = (value) => value !== null && value !== undefined;
const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
const text = (field, value, max, multiline = false) => present(value) ? boundedText(field, value, max, { multiline }) : null;
function whole(field, value, min, max) {
    if (!present(value))
        return null;
    if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
        throw fail(field, `must be a whole number from ${min} to ${max}`);
    }
    return value;
}
function oneOf(field, tuple, value) {
    if (!present(value))
        return null;
    if (!isMember(tuple, value))
        throw fail(field, "must be a known value");
    return value;
}
/** The typed subtype of an item: it must match the item type. Shared by the draft and the subtype edit. */
export function validateCatalogSubtype(itemType, subtype) {
    if (subtype?.type !== itemType)
        throw fail("subtype", "must match the item type");
    switch (subtype.type) {
        case "DINING": {
            const spend = subtype.typicalSpendPerPerson ?? null;
            return {
                type: "DINING",
                dressCode: oneOf("subtype.dressCode", diningDressCodes, subtype.dressCode),
                reservationPolicy: oneOf("subtype.reservationPolicy", diningReservationPolicies, subtype.reservationPolicy),
                reservationNotes: text("subtype.reservationNotes", subtype.reservationNotes, 1000, true),
                typicalSpendPerPerson: spend === null
                    ? null
                    : {
                        amountMinor: minorUnits(spend.amountMinor),
                        currency: supportedCurrency(spend.currency),
                    },
            };
        }
        case "VILLA":
            return {
                type: "VILLA",
                bedrooms: whole("subtype.bedrooms", subtype.bedrooms, 0, 50),
                bathrooms: whole("subtype.bathrooms", subtype.bathrooms, 0, 50),
                maxGuests: whole("subtype.maxGuests", subtype.maxGuests, 1, 100),
                minimumStayNights: whole("subtype.minimumStayNights", subtype.minimumStayNights, 1, 365),
                checkInTime: present(subtype.checkInTime)
                    ? localTimeOfDay(subtype.checkInTime, "subtype.checkInTime")
                    : null,
                checkOutTime: present(subtype.checkOutTime)
                    ? localTimeOfDay(subtype.checkOutTime, "subtype.checkOutTime")
                    : null,
            };
        case "EVENT": {
            if (!isValidDate(subtype.startsAt))
                throw fail("subtype.startsAt", "must be a valid date");
            const endsAt = subtype.endsAt ?? null;
            if (endsAt !== null) {
                if (!isValidDate(endsAt))
                    throw fail("subtype.endsAt", "must be a valid date");
                if (endsAt.getTime() < subtype.startsAt.getTime()) {
                    throw fail("subtype.endsAt", "must not be before the start");
                }
            }
            const timeZone = ianaTimeZone(subtype.timeZone, "subtype.timeZone");
            const schedulePrecision = oneOf("subtype.schedulePrecision", eventSchedulePrecisions, subtype.schedulePrecision) ??
                "EXACT";
            // A date without a time is stored as local midnight, so the instant still means the date.
            if (schedulePrecision === "DATE_ONLY") {
                if (!isLocalMidnight(subtype.startsAt, timeZone)) {
                    throw fail("subtype.startsAt", "must be local midnight for a date-only event");
                }
                if (endsAt !== null && !isLocalMidnight(endsAt, timeZone)) {
                    throw fail("subtype.endsAt", "must be local midnight for a date-only event");
                }
            }
            return {
                type: "EVENT",
                venueName: text("subtype.venueName", subtype.venueName, 200),
                startsAt: subtype.startsAt,
                endsAt,
                timeZone,
                schedulePrecision,
                eventStatus: oneOf("subtype.eventStatus", eventStatuses, subtype.eventStatus) ?? "SCHEDULED",
                ticketingNotes: text("subtype.ticketingNotes", subtype.ticketingNotes, 1000, true),
            };
        }
        case "EXPERIENCE": {
            const groupMin = whole("subtype.groupMin", subtype.groupMin, 1, 500);
            const groupMax = whole("subtype.groupMax", subtype.groupMax, 1, 500);
            if (groupMin !== null && groupMax !== null && groupMin > groupMax) {
                throw fail("subtype.groupMin", "must not be above the maximum");
            }
            return {
                type: "EXPERIENCE",
                durationMinutes: whole("subtype.durationMinutes", subtype.durationMinutes, 1, 10_080),
                privacy: oneOf("subtype.privacy", experiencePrivacies, subtype.privacy),
                groupMin,
                groupMax,
                minimumAge: whole("subtype.minimumAge", subtype.minimumAge, 0, 120),
                eligibilityNotes: text("subtype.eligibilityNotes", subtype.eligibilityNotes, 2000, true),
                includes: text("subtype.includes", subtype.includes, 2000, true),
                excludes: text("subtype.excludes", subtype.excludes, 2000, true),
                meetingPointText: text("subtype.meetingPointText", subtype.meetingPointText, 300),
            };
        }
        case "SERVICE":
            return {
                type: "SERVICE",
                serviceAreaText: text("subtype.serviceAreaText", subtype.serviceAreaText, 300),
                leadTimeHours: whole("subtype.leadTimeHours", subtype.leadTimeHours, 0, 8760),
                availabilityNotes: text("subtype.availabilityNotes", subtype.availabilityNotes, 1000, true),
            };
    }
}
function listOf(field, value, max) {
    if (!present(value))
        return [];
    if (!Array.isArray(value) || value.length > max)
        throw fail(field, `must hold at most ${max}`);
    return value;
}
/**
 * The fulfilment mode and the link that goes with it. DIRECT_API is storable but refused
 * (`catalog.fulfilment_mode_not_enabled`). The three database CHECKs on the link say the same.
 */
export function validateCatalogFulfilment(mode, link) {
    const fulfilmentMode = mode ?? "HUMAN_CONCIERGE";
    if (!isMember(catalogFulfilmentModes, fulfilmentMode)) {
        throw fail("fulfilmentMode", "must be a known mode");
    }
    if (!isFulfilmentModeEnabled(fulfilmentMode)) {
        throw violation("catalog.fulfilment_mode_not_enabled");
    }
    const externalUrl = present(link) ? link : null;
    if (externalUrl !== null) {
        if (typeof externalUrl !== "string" ||
            externalUrl.length > 2048 ||
            !EXTERNAL_URL.test(externalUrl)) {
            throw fail("externalUrl", "must be an https link of at most 2048 characters");
        }
    }
    if ((fulfilmentMode === "EXTERNAL_LINK" || fulfilmentMode === "CUSTOMER_SELF_SERVICE") &&
        externalUrl === null) {
        throw fail("externalUrl", "is required for this fulfilment mode");
    }
    // The database CHECK `catalog_items_external_url_mode` says the same: a concierge item shows no
    // link, so a customer cannot bypass the concierge and no supplier booking page leaks (owner Q4).
    if (fulfilmentMode === "HUMAN_CONCIERGE" && externalUrl !== null) {
        throw fail("externalUrl", "is not allowed for this fulfilment mode");
    }
    return { fulfilmentMode, externalUrl };
}
/** The country, and the point: a latitude needs a longitude and the other way round. */
export function validateCatalogLocation(country, lat, lng) {
    const countryCode = country ?? null;
    if (countryCode !== null && (typeof countryCode !== "string" || !COUNTRY.test(countryCode))) {
        throw fail("countryCode", "must be a two-letter uppercase country code");
    }
    const latitude = lat ?? null;
    const longitude = lng ?? null;
    if ((latitude === null) !== (longitude === null)) {
        throw fail("latitude", "needs a longitude and the other way round");
    }
    if (latitude !== null && longitude !== null) {
        if (typeof latitude !== "number" || !(latitude >= -90 && latitude <= 90)) {
            throw fail("latitude", "must be from -90 to 90");
        }
        if (typeof longitude !== "number" || !(longitude >= -180 && longitude <= 180)) {
            throw fail("longitude", "must be from -180 to 180");
        }
    }
    return { countryCode, latitude, longitude };
}
/** The editorial text and the time zone of the core. Shared by the draft and the details edit. */
export function validateCatalogCoreFields(input) {
    return {
        name: boundedText("name", input.name, 200),
        slug: catalogSlug(input.slug),
        shortDescription: text("shortDescription", input.shortDescription, 300),
        description: text("description", input.description, 8000, true),
        addressText: text("addressText", input.addressText, 300),
        locality: text("locality", input.locality, 100),
        region: text("region", input.region, 100),
        timeZone: present(input.timeZone) ? ianaTimeZone(input.timeZone, "timeZone") : null,
        editorialPriority: whole("editorialPriority", input.editorialPriority, 0, 1000) ?? 0,
    };
}
export function validateCatalogCategories(value) {
    const categories = listOf("categories", value, MAX_CATEGORIES).map((category) => {
        if (typeof category.code !== "string" || !CODE.test(category.code)) {
            throw fail("categories", "has a code that is not a lowercase code");
        }
        return { code: category.code, isPrimary: category.isPrimary === true };
    });
    if (new Set(categories.map((category) => category.code)).size !== categories.length) {
        throw fail("categories", "must not repeat a category");
    }
    if (categories.filter((category) => category.isPrimary).length > 1) {
        throw fail("categories", "has at most one primary category");
    }
    return Object.freeze(categories);
}
export function validateCatalogTags(value) {
    const tags = listOf("tags", value, MAX_TAGS).map((tag) => {
        if (!isMember(catalogTagNamespaces, tag.namespace) || !CODE.test(tag.code)) {
            throw fail("tags", "has an unknown namespace or a code that is not a lowercase code");
        }
        return { namespace: tag.namespace, code: tag.code };
    });
    if (new Set(tags.map((tag) => `${tag.namespace}:${tag.code}`)).size !== tags.length) {
        throw fail("tags", "must not repeat a tag");
    }
    return Object.freeze(tags);
}
/** The list is the order on the page: position is the index. A path must not repeat. */
export function validateCatalogMedia(value) {
    const media = listOf("media", value, MAX_MEDIA).map((item, position) => {
        if (!isMember(catalogMediaKinds, item.kind))
            throw fail("media", "has an unknown kind");
        if (typeof item.storageObjectPath !== "string" ||
            item.storageObjectPath.length > 512 ||
            !MEDIA_PATH.test(item.storageObjectPath) ||
            item.storageObjectPath.startsWith("/") ||
            item.storageObjectPath.split("/").some((segment) => segment === "" || segment === "..")) {
            throw fail("media", "has a path that is not a Lafayette storage path");
        }
        const isCover = item.isCover === true;
        if (isCover && item.kind !== "IMAGE")
            throw fail("media", "has a cover that is not an image");
        return {
            position,
            kind: item.kind,
            storageObjectPath: item.storageObjectPath,
            altText: text("media.altText", item.altText, 300),
            caption: text("media.caption", item.caption, 300),
            credit: text("media.credit", item.credit, 200),
            isCover,
        };
    });
    if (media.filter((item) => item.isCover).length > 1) {
        throw fail("media", "has at most one cover");
    }
    if (new Set(media.map((item) => item.storageObjectPath)).size !== media.length) {
        throw fail("media", "must not repeat a file");
    }
    return Object.freeze(media);
}
export function validateCatalogPeople(value) {
    const people = listOf("people", value, MAX_PEOPLE).map((person, position) => {
        if (!isMember(catalogPersonRoles, person.role))
            throw fail("people", "has an unknown role");
        return { position, slug: catalogSlug(person.slug), role: person.role };
    });
    if (new Set(people.map((person) => `${person.slug}:${person.role}`)).size !== people.length) {
        throw fail("people", "must not repeat a person in one role");
    }
    return Object.freeze(people);
}
/** One external reference per provider. */
export function validateCatalogExternalRefs(value) {
    const externalRefs = listOf("externalRefs", value, MAX_EXTERNAL_REFS).map((ref) => {
        if (typeof ref.provider !== "string" || !PROVIDER.test(ref.provider)) {
            throw fail("externalRefs", "has a provider that is not a lowercase provider code");
        }
        if (!isValidDate(ref.observedAt))
            throw fail("externalRefs", "has an invalid observation time");
        return {
            provider: ref.provider,
            externalId: boundedText("externalRefs.externalId", ref.externalId, 255),
            observedAt: ref.observedAt,
        };
    });
    if (new Set(externalRefs.map((ref) => ref.provider)).size !== externalRefs.length) {
        throw fail("externalRefs", "has at most one reference per provider");
    }
    return Object.freeze(externalRefs);
}
/**
 * Validates a new catalog item: the core, its typed subtype (which must match the item type), the
 * price (through `validateCatalogPricing`, never payment-grade), the fulfilment mode (DIRECT_API is
 * storable but refused: `catalog.fulfilment_mode_not_enabled`), and the links: categories (at most
 * one primary), namespaced tags, media (a Lafayette storage path, one cover, an image), people,
 * external references (one per provider) and source records (internal provenance; none repeated).
 * Positions are assigned here, never by the caller. Messages
 * name a field and a rule, never a value. Whether the item may be PUBLISHED is a later question
 * (`assessPublishability`); a draft may be incomplete.
 */
export function validateCatalogDraft(input) {
    if (!isMember(catalogItemTypes, input.itemType))
        throw fail("itemType", "must be a known type");
    if (!isMember(catalogSourceKinds, input.sourceKind)) {
        throw fail("sourceKind", "must be a known source");
    }
    const { fulfilmentMode, externalUrl } = validateCatalogFulfilment(input.fulfilmentMode, input.externalUrl);
    const { countryCode, latitude, longitude } = validateCatalogLocation(input.countryCode, input.latitude, input.longitude);
    const categories = validateCatalogCategories(input.categories);
    const tags = validateCatalogTags(input.tags);
    const media = validateCatalogMedia(input.media);
    const people = validateCatalogPeople(input.people);
    const externalRefs = validateCatalogExternalRefs(input.externalRefs);
    const sources = listOf("sources", input.sources, MAX_SOURCES).map((source) => {
        try {
            return {
                system: catalogSourceSystem(source.system),
                key: catalogSourceKey(source.key),
            };
        }
        catch (error) {
            if (!(error instanceof DomainValidationError))
                throw error;
            throw fail("sources", "has a system or key that is not valid");
        }
    });
    if (new Set(sources.map((source) => `${source.system}\u0000${source.key}`)).size !== sources.length) {
        throw fail("sources", "must not repeat a source record");
    }
    const core = validateCatalogCoreFields(input);
    return Object.freeze({
        itemType: input.itemType,
        name: core.name,
        slug: core.slug,
        shortDescription: core.shortDescription,
        description: core.description,
        addressText: core.addressText,
        locality: core.locality,
        region: core.region,
        countryCode,
        latitude,
        longitude,
        timeZone: core.timeZone,
        pricing: validateCatalogPricing(input.pricing ?? {
            model: "UNKNOWN",
            currency: null,
            amountMinor: null,
            minAmountMinor: null,
            maxAmountMinor: null,
            unit: null,
            terms: null,
            source: null,
            validFrom: null,
            validUntil: null,
            noExpiry: false,
        }),
        fulfilmentMode,
        externalUrl,
        editorialPriority: core.editorialPriority,
        sourceKind: input.sourceKind,
        subtype: validateCatalogSubtype(input.itemType, input.subtype),
        categories,
        tags,
        media,
        people,
        externalRefs,
        sources: Object.freeze(sources),
    });
}
